/**
 * CEO Briefing — the screen a chief executive reads before the project review.
 *
 * Headline and status, the schedule and cost outlook with forecasts, the risks
 * ranked and openable onto their evidence, the decisions only the CEO can take,
 * the commercial exposure, a watchlist, the questions to ask in the room, the
 * 30-day priorities and the module health grid — every finding links back to
 * the analyst that produced it, and the Chief of Staff answers anything else.
 *
 * Backed by:
 *   POST /api/v1/projects/{pid}/ai-analyst/ceo-briefing?force=true  (job + progress)
 *   GET  /api/v1/projects/{pid}/ai-analyst/ceo-briefing/latest
 *   GET  /api/v1/projects/{pid}/ai-analyst/ceo-briefing/export.pdf
 *   POST /api/v1/projects/{pid}/ai-analyst/ceo-briefing/email | /chat
 */
import { useEffect, useRef, useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useProject } from '@/contexts/ProjectContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  AlertOctagon, CalendarClock, ChevronDown, ChevronUp, Crown, Copy, DollarSign, Download, Gavel,
  HelpCircle, Loader2, Mail, RefreshCw, Scale, Sparkles, Target, Eye,
} from 'lucide-react';
import {
  ChiefOfStaffChat, ChiefOfStaffButton,
} from '@/components/ceo/ChiefOfStaffChat';
import {
  EmailDialog, Expandable, Field, ModuleHealthGrid, ModuleLink, RiskCard, Section, SectionFold,
  StatusPill, Trend, STATUS_META,
} from '@/components/ceo/BriefingParts';

const PROGRESS_LABEL = 'Cognara is reading every module analyst and writing the briefing';

export default function CEOBriefing() {
  const { projectId, projectName, loading: projectLoading } = useProject();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const stop = useRef(false);

  useEffect(() => () => { stop.current = true; }, []);

  useEffect(() => {
    if (!projectId) { setRecord(null); return; }
    setLoading(true);
    api.get(`/v1/projects/${projectId}/ai-analyst/ceo-briefing/latest`)
      .then(r => setRecord(r.data))
      .catch(e => { if (e?.response?.status !== 404) console.warn(e); setRecord(null); })
      .finally(() => setLoading(false));
  }, [projectId]);

  const run = async () => {
    if (!projectId || running) return;
    setRunning(true);
    setProgress({ percent: 1, stage: 'Starting', elapsedSeconds: 0 });
    try {
      const r = await api.post(`/v1/projects/${projectId}/ai-analyst/ceo-briefing?force=true`,
        {}, { timeout: 60000 });
      if (r.data?.status === 'done' && r.data?.briefing) {
        setRecord(r.data);
        toast.success('Briefing refreshed');
        return;
      }
      const jobId = r.data?.jobId;
      if (!jobId) { setRecord(r.data); return; }
      for (let attempt = 0; attempt < 600; attempt += 1) {
        await new Promise(res => setTimeout(res, 3000));
        if (stop.current) return;
        try {
          const s = await api.get(
            `/v1/projects/${projectId}/ai-analyst/ceo-briefing/status/${jobId}`,
            { timeout: 20000 });
          setProgress({
            percent: s.data?.percent ?? 1,
            stage: s.data?.stage || 'Working',
            detail: s.data?.stageDetail || '',
            elapsedSeconds: s.data?.elapsedSeconds ?? 0,
            etaSeconds: s.data?.etaSeconds,
          });
          if (s.data?.status === 'done' && s.data?.briefing) {
            setRecord(s.data.briefing);
            setProgress({ percent: 100, stage: 'Done' });
            toast.success('Briefing refreshed');
            return;
          }
          if (s.data?.status === 'failed') {
            toast.error(`Briefing failed: ${s.data.error || 'unknown error'}`);
            return;
          }
        } catch (pollErr) { console.warn('briefing poll', pollErr); }
      }
      toast.error('The briefing is taking unusually long — try again.');
    } catch (e) {
      toast.error(`Briefing failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/v1/projects/${projectId}/ai-analyst/ceo-briefing/export.pdf`,
        { responseType: 'blob', timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ceo-briefing-${(projectName || projectId || 'project').replace(/\s+/g, '-').toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The PDF could not be built');
    }
  };

  const b = record?.briefing || {};
  const overall = STATUS_META[(b.overall_status || 'UNKNOWN').toUpperCase()] || STATUS_META.UNKNOWN;
  const coverage = record?.moduleCoverage;
  const so = b.schedule_outlook || {};
  const co = b.cost_outlook || {};
  const ce = b.commercial_exposure || {};

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-5 space-y-4" data-testid="ceo-briefing-page">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              <h1 className="text-2xl font-bold text-foreground">CEO Briefing</h1>
              {b.overall_status && <StatusPill status={b.overall_status} testId="ceo-briefing-overall-status" />}
            </div>
            <p className="text-sm text-muted-foreground">
              {projectName || 'this project'} · written from every module analyst by Cognara
            </p>
            {coverage && (
              <p className="text-[10px] text-muted-foreground mt-1" data-testid="ceo-briefing-coverage">
                {coverage.withData} of {coverage.total} modules with data
                {record?.generatedAt && ` · generated ${new Date(record.generatedAt).toLocaleString()}`}
                {record?.generationSeconds ? ` · took ${record.generationSeconds}s` : ''}
              </p>
            )}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {record && <ChiefOfStaffButton onClick={() => setChatOpen(true)} />}
            {record && (
              <>
                <button onClick={downloadPdf} data-testid="ceo-briefing-pdf"
                        className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border
                                   border-border hover:bg-muted/40 transition-colors">
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
                <button onClick={() => setEmailOpen(true)} data-testid="ceo-briefing-email"
                        className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border
                                   border-border hover:bg-muted/40 transition-colors">
                  <Mail className="w-3.5 h-3.5" /> Email
                </button>
                <button onClick={() => { navigator.clipboard.writeText(JSON.stringify(b, null, 2)); toast.success('Copied'); }}
                        data-testid="ceo-briefing-copy"
                        className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border
                                   border-border hover:bg-muted/40 transition-colors">
                  <Copy className="w-3.5 h-3.5" /> JSON
                </button>
              </>
            )}
            <button onClick={run} disabled={running || !projectId} data-testid="ceo-briefing-run"
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg
                               bg-amber-500 text-black disabled:opacity-50">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                       : <RefreshCw className="w-3.5 h-3.5" />}
              {record ? 'Refresh briefing' : 'Generate briefing'}
            </button>
          </div>
        </div>

        {running && progress && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-2"
               data-testid="ceo-briefing-progress">
            <div className="flex items-center gap-2 text-xs text-foreground">
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              <span className="font-semibold">{progress.stage}</span>
              {progress.detail && <span className="text-muted-foreground">· {progress.detail}</span>}
              <span className="ml-auto font-mono text-[11px]" data-testid="ceo-briefing-percent">
                {progress.percent}%
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-amber-500 transition-all duration-700"
                   style={{ width: `${progress.percent}%` }} />
            </div>
            <p className="text-[10px] text-muted-foreground">
              {PROGRESS_LABEL} · {progress.elapsedSeconds || 0}s elapsed
              {progress.etaSeconds ? ` · about ${progress.etaSeconds}s left` : ''}
            </p>
          </div>
        )}

        {!projectId && !projectLoading && (
          <Empty title="Pick a project first"
                 sub="The briefing is project-scoped. Open a project, then come back." />
        )}
        {projectId && loading && (
          <div className="py-10 text-center text-muted-foreground" data-testid="ceo-briefing-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            <p className="text-xs mt-2">Loading the last briefing…</p>
          </div>
        )}
        {projectId && !loading && !record && !running && (
          <Empty title="No briefing yet"
                 sub="Generate it — every module analyst is read and each section of the briefing is written in turn." />
        )}

        {record && (
          <>
            {(record.sectionFailures || []).length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3"
                   data-testid="ceo-briefing-section-failures">
                <p className="text-[11px] text-foreground">
                  {record.sectionFailures.length} section(s) could not be written this run:{' '}
                  {record.sectionFailures.map(f => f.section).join(', ')}. Everything else below is
                  current — refresh to try those again.
                </p>
              </div>
            )}

            <div className="rounded-xl p-5"
                 style={{ background: `linear-gradient(135deg, ${overall.fg}11, transparent)`,
                          border: `1px solid ${overall.fg}44` }}
                 data-testid="ceo-briefing-hero">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Executive headline
              </p>
              <p className="text-xl font-bold text-foreground mt-1" data-testid="ceo-briefing-headline">
                {b.executive_one_liner || '—'}
              </p>
              <p className="text-sm text-muted-foreground mt-2">{b.overall_narrative}</p>
              <div className="flex flex-wrap gap-4 mt-3 text-[11px] text-muted-foreground">
                {b.confidence && <span>confidence: <b className="text-foreground">{b.confidence}</b></span>}
                {b.data_caveat && <span>caveat: {b.data_caveat}</span>}
              </div>
            </div>

            {(b.top_risks || []).length > 0 && (
              <Section icon={AlertOctagon} title="Risks the CEO must act on"
                       note="open any one for the evidence behind it" testId="ceo-risks">
                <div className="space-y-2">
                  {b.top_risks.map((r, i) => <RiskCard key={i} risk={r} index={i} />)}
                </div>
              </Section>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <Section icon={CalendarClock} title="Schedule outlook" testId="ceo-schedule-outlook"
                       right={<Trend trend={so.trend} />}>
                <p className="text-sm text-foreground">{b.schedule_pulse}</p>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Field label="Forecast completion" value={so.forecast_completion} />
                  <Field label="Variance (days)" value={so.variance_days} />
                </div>
                <Detail label="the schedule read" count={(so.drivers || []).length} testId="schedule">
                  {so.narrative && <p className="text-xs text-muted-foreground">{so.narrative}</p>}
                  <Drivers rows={so.drivers} testId="schedule-drivers" />
                </Detail>
              </Section>

              <Section icon={DollarSign} title="Cost outlook" testId="ceo-cost-outlook"
                       right={<Trend trend={co.trend} />}>
                <p className="text-sm text-foreground">{b.cost_pulse}</p>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Field label="Cost at completion" value={co.cost_at_completion} />
                  <Field label="Variance at completion" value={co.variance_at_completion} />
                  <Field label="Cash exposure" value={co.cash_exposure} />
                </div>
                <Detail label="the cost read" count={(co.drivers || []).length} testId="cost">
                  {co.narrative && <p className="text-xs text-muted-foreground">{co.narrative}</p>}
                  <Drivers rows={co.drivers} testId="cost-drivers" />
                </Detail>
              </Section>
            </div>

            {(b.decisions_required || []).length > 0 && (
              <Section icon={Gavel} title="Decisions to authorise"
                       note="open any one for the options" testId="ceo-decisions">
                <div className="grid gap-2 lg:grid-cols-2">
                  {b.decisions_required.map((d, i) => (
                    <Expandable key={i} testId={`decision-card-${i}`} title={d.decision}
                                meta={`${d.owner} · by ${d.by_when}`}>
                      <p className="text-[11px] text-muted-foreground">{d.context}</p>
                      <div className="space-y-1.5">
                        {(d.options || []).map((o, j) => (
                          <div key={j} className="text-[11px] flex gap-2">
                            <span className="text-foreground/40">◦</span>
                            <span><b className="text-foreground">{o.option}</b>
                              <span className="text-muted-foreground"> — {o.consequence}</span></span>
                          </div>
                        ))}
                      </div>
                      <div className="rounded-lg p-3" style={{ background: 'rgba(16,185,129,0.08)' }}>
                        <p className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold">
                          Recommended
                        </p>
                        <p className="text-xs text-foreground mt-1">{d.recommendation}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Cost of delay" value={d.cost_of_delay} />
                        <Field label="Source" value={<ModuleLink moduleKey={d.module} />} />
                      </div>
                    </Expandable>
                  ))}
                </div>
              </Section>
            )}

            {ce.headline && (
              <Section icon={Scale} title="Commercial and claims exposure" testId="ceo-commercial">
                <SectionFold testId="commercial" label="the exposure line by line"
                             count={(ce.items || []).length}
                             summary={(
                               <>
                                 <p className="text-sm text-foreground">{ce.headline}</p>
                                 <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mt-3">
                                   <Field label="Claims" value={ce.claims_position} />
                                   <Field label="Extension of time" value={ce.eot_position} />
                                   <Field label="Liquidated damages" value={ce.ld_position} />
                                   <Field label="Variations" value={ce.variations_position} />
                                 </div>
                               </>
                             )}>
                  <div className="space-y-1.5">
                    {(ce.items || []).map((it, i) => (
                      <div key={i} className="flex items-center gap-3 text-[11px] border-b
                                               border-border/60 pb-1.5" data-testid={`commercial-item-${i}`}>
                        <span className="text-foreground flex-1">{it.item}</span>
                        <span className="font-mono text-muted-foreground">{it.value}</span>
                        <span className="text-muted-foreground">{it.status}</span>
                        <ModuleLink moduleKey={it.module} />
                      </div>
                    ))}
                  </div>
                </SectionFold>
              </Section>
            )}

            {(b.watchlist || []).length > 0 && (
              <Section icon={Eye} title="Watchlist" note="not red yet, but moving"
                       testId="ceo-watchlist">
                <div className="grid gap-2 md:grid-cols-2">
                  {b.watchlist.map((w, i) => (
                    <Expandable key={i} testId={`watch-item-${i}`} title={w.indicator}
                                meta={`now ${w.current} · threshold ${w.threshold} · ${(w.trend || '').toLowerCase()}`}>
                      <p className="text-[11px] text-muted-foreground">{w.why_it_matters}</p>
                      <ModuleLink moduleKey={w.module} />
                    </Expandable>
                  ))}
                </div>
              </Section>
            )}

            {(b.questions_to_ask || []).length > 0 && (
              <Section icon={HelpCircle} title="Questions to ask in the review"
                       note="open one for why it matters" testId="ceo-questions">
                <div className="space-y-2">
                  {b.questions_to_ask.map((q, i) => (
                    <Expandable key={i} testId={`question-${i}`} title={q.question}
                                meta={`to ${q.to_whom}`}>
                      <p className="text-[11px] text-muted-foreground">{q.why}</p>
                      <p className="text-[11px] text-emerald-600">
                        a good answer: {q.good_answer_looks_like}
                      </p>
                      <ModuleLink moduleKey={q.module} />
                    </Expandable>
                  ))}
                </div>
              </Section>
            )}

            {(b.next_30_day_priorities || []).length > 0 && (
              <Section icon={Target} title="Next 30 days" testId="ceo-priorities">
                <div className="space-y-2">
                  {b.next_30_day_priorities.map((p, i) => (
                    <Expandable key={i} testId={`priority-${i}`} title={`${i + 1}. ${p.priority}`}
                                meta={p.owner}>
                      <Field label="Expected outcome" value={p.expected_outcome} />
                      <Field label="First step" value={p.first_step} />
                      <ModuleLink moduleKey={p.module} />
                    </Expandable>
                  ))}
                </div>
              </Section>
            )}

            {(b.module_health_summary || []).length > 0 && (
              <Section title="Module health" note="click any module to open its analyst"
                       testId="ceo-module-health">
                <SectionFold testId="module-health" label="the module grid"
                             count={b.module_health_summary.length}
                             summary={<HealthCounts modules={b.module_health_summary} />}>
                  <ModuleHealthGrid modules={b.module_health_summary} />
                </SectionFold>
              </Section>
            )}
          </>
        )}
      </div>

      <ChiefOfStaffChat projectId={projectId} open={chatOpen} onClose={() => setChatOpen(false)} />
      <EmailDialog projectId={projectId} open={emailOpen} onClose={() => setEmailOpen(false)} />
    </DashboardLayout>
  );
}

function HealthCounts({ modules }) {
  const counts = (modules || []).reduce((acc, m) => {
    const s = (m.status || 'UNKNOWN').toUpperCase();
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  return (
    <div className="flex flex-wrap gap-2" data-testid="module-health-counts">
      {['RED', 'AMBER', 'GREEN', 'UNKNOWN'].map(s => (
        <span key={s} className="text-[11px] px-2.5 py-1 rounded-lg"
              style={{ background: STATUS_META[s].bg, color: STATUS_META[s].fg }}
              data-testid={`module-health-count-${s}`}>
          {STATUS_META[s].label} {counts[s] || 0}
        </span>
      ))}
    </div>
  );
}

function Detail({ label, count, testId, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 pt-3 border-t border-border/60">
      <button onClick={() => setOpen(o => !o)} data-testid={`${testId}-detail-toggle`}
              className="flex items-center gap-1.5 text-[11px] text-muted-foreground
                         hover:text-foreground transition-colors">
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        {open ? `Hide ${label}` : `Show ${label}`}
        {count > 0 && <span className="font-mono">· {count} drivers</span>}
      </button>
      {open && (
        <div className="mt-3 space-y-3" data-testid={`${testId}-detail`}>{children}</div>
      )}
    </div>
  );
}

function Drivers({ rows, testId }) {
  if (!(rows || []).length) return null;
  return (
    <div className="mt-3 space-y-1.5" data-testid={testId}>
      {rows.map((d, i) => (
        <div key={i} className="text-[11px] flex gap-2">
          <span className="text-foreground/40">—</span>
          <span>
            <b className="text-foreground">{d.driver}</b>
            <span className="text-muted-foreground"> · {d.evidence} </span>
            <ModuleLink moduleKey={d.module} />
          </span>
        </div>
      ))}
    </div>
  );
}

function Empty({ title, sub }) {
  return (
    <div className="rounded-xl border border-dashed border-border py-12 text-center"
         data-testid="ceo-briefing-empty">
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">{sub}</p>
    </div>
  );
}
